import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GoogleMaps } from '../../../components';
import {
  rashDrivingMarker,
  sosAlertMarker,
  actualCheckPointMarker,
  driverDistractionMarker,
  driverDrowsinessMarker,
  doorOpenMarker,
  overspeedMarker,
  gpsPointMarker,
} from '../../../utilities/markersData';
import { DMS_TYPE, VEHICLE_STATUS } from '../../../utilities/constants';
import FullScreenToggle from './FullScreenToggle';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { defaultCenter, samplePathData } from '../../../utilities/dummyData';
import AddressFetcher from './AddressFetcher';
import {
  getFormattedTimeFromTimestamp,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import useApiPolling from '../../../hooks/useApiPolling';
import { Typography } from 'antd';
import { ANOMALY_BG_COLOR } from '../../../utilities/constants';

const processedTrips = {};
const { Text } = Typography;

const TripTracking = ({
  isFullScreen,
  onFullScreenToggle,
  selectedTrip,
  activeTabKey,
  pageActiveKey,
  updateLastUpdatedTime = () => {},
  selectedAnomlayId = null,
  showCustomFullScreenControl = false,
}) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [infoWindow, setInfoWindow] = useState({});
  const [isGPSMarkerUpdated, setIsGPSMarkerUpdated] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [pathData, setPathData] = useState({ ...samplePathData, path: [] });
  const [center, setCenter] = useState(defaultCenter);
  const [gpsLoadingMessage, setGpsLoadingMessage] = useState(null);
  const pastGpsDataLoadedRef = useRef(false);
  const initialGpsLoadCompleteRef = useRef(true);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const handleMarkerClick = (key, position, content, title, children) => {
    setInfoWindow({ title, content, position, children, key, isVisible: true });
  };
  const abortFetchRef = useRef(null);
  const abortControllersRef = useRef([]);
  const apiCallStatusRef = useRef({
    sosAndRashdriving: false,
    pickupDrop: false,
    gpsData: false,
  });
  const currentTripIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const [, setLivePassengersCount] = useState(0);

  const abortAllRequests = () => {
    abortControllersRef.current.forEach(controller => {
      try {
        controller.abort();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error aborting controller:', error);
      }
    });
    abortControllersRef.current = [];
  };

  const resetTrackingState = () => {
    setPollingEnabled(false);
    setMarkers([]);
    setLivePassengersCount(0);
    setPathData({ ...samplePathData, path: [] });
    setCenter(defaultCenter);
    setInfoWindow({});
    setGpsLoadingMessage({ message: null });
    setIsGPSMarkerUpdated(false);
    pastGpsDataLoadedRef.current = false;
    initialGpsLoadCompleteRef.current = true;
    apiCallStatusRef.current = {
      sosAndRashdriving: false,
      pickupDrop: false,
      gpsData: false,
    };
  };

  const updateLivePassengersCount = data => {
    if (!data) return;

    if (data.pickup_time && !data.drop_time) {
      setLivePassengersCount(prev => prev + 1);
    } else if (data.drop_time) {
      setLivePassengersCount(prev => Math.max(0, prev - 1));
    }
  };

  const updateGPSMarkers = data => {
    if (!data?.latitude || !data?.longitude || !selectedTrip?.trip_id) return;

    setIsGPSMarkerUpdated(true);
    setMarkers(prev => {
      const livePinIndex = prev.findIndex(
        marker => marker?.key === `live-location-${selectedTrip?.trip_id}`
      );

      const newMarker = {
        ...gpsPointMarker,
        position: {
          lat: Number(data.latitude),
          lng: Number(data.longitude),
        },
        key: `live-location-${selectedTrip?.trip_id}`,
      };

      if (livePinIndex !== -1) {
        const updatedMarkers = [...prev];
        updatedMarkers[livePinIndex] = newMarker;
        return updatedMarkers;
      } else {
        return [...prev, newMarker];
      }
    });
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedTrip?.trip_id) {
      currentTripIdRef.current = null;

      if (abortFetchRef.current) {
        abortFetchRef.current();
        abortFetchRef.current = null;
      }

      abortAllRequests();
      resetTrackingState();

      Object.keys(processedTrips).forEach(key => {
        delete processedTrips[key];
      });
    }
  }, [selectedTrip?.trip_id]);

  useEffect(() => {
    currentTripIdRef.current = null;
    resetTrackingState();

    if (abortFetchRef.current) {
      abortFetchRef.current();
      abortFetchRef.current = null;
    }

    abortAllRequests();

    Object.keys(processedTrips).forEach(key => {
      delete processedTrips[key];
    });
  }, [activeTabKey]);

  useEffect(() => {
    if (!selectedTrip?.trip_id) {
      return;
    }
    currentTripIdRef.current = selectedTrip.trip_id;

    // Reset API call tracking
    apiCallStatusRef.current = {
      sosAndRashdriving: false,
      pickupDrop: false,
      gpsData: false,
    };

    // Abort all existing requests
    if (abortFetchRef.current) {
      abortFetchRef.current();
      abortFetchRef.current = null;
    }

    const abortAllRequests = () => {
      abortControllersRef.current.forEach(controller => {
        try {
          controller.abort();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error aborting controller:', error);
        }
      });
      abortControllersRef.current = [];
    };
    abortAllRequests();

    // Reset states
    resetTrackingState();

    const tripKey = `${selectedTrip.trip_id}-${activeTabKey}`;

    const timeoutId = setTimeout(async () => {
      if (
        !isMountedRef.current ||
        currentTripIdRef.current !== selectedTrip.trip_id
      ) {
        return;
      }

      if (processedTrips[tripKey]) {
        return;
      }

      // Mark this trip as being processed
      processedTrips[tripKey] = true;

      try {
        const payload = {
          deviceId: selectedTrip?.ridebuddy_deviceid,
          tripIds: [selectedTrip?.trip_id],
        };

        await getPickupDropData(payload);

        if (
          currentTripIdRef.current !== selectedTrip?.trip_id ||
          !isMountedRef.current
        )
          return;

        await getSOSAndRashdrivingdetails(payload);

        if (
          currentTripIdRef.current !== selectedTrip?.trip_id ||
          !isMountedRef.current
        )
          return;

        await getGPSData(payload, true);

        if (
          currentTripIdRef.current === selectedTrip?.trip_id &&
          isMountedRef.current
        ) {
          initialGpsLoadCompleteRef.current = false;
          setPollingEnabled(true);
        }
        const tripKey = `${selectedTrip.trip_id}-${activeTabKey}`;
        delete processedTrips[tripKey];
      } catch (error) {
        if (error.name === 'AbortError') {
          // Fetch aborted
        } else {
          if (
            isMountedRef.current &&
            currentTripIdRef.current === selectedTrip?.trip_id
          ) {
            addErrorMessage(
              getErrorMessage(
                error,
                'Unable to fetch trip details! Please try again after sometime.'
              )
            );
            setGpsLoadingMessage({
              message: null,
            });
          }
          const tripKey = `${selectedTrip.trip_id}-${activeTabKey}`;
          delete processedTrips[tripKey];
        }
      }
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (abortFetchRef.current) {
        abortFetchRef.current();
        abortFetchRef.current = null;
      }
      abortAllRequests();
    };
  }, [selectedTrip?.trip_id, activeTabKey, addErrorMessage]);

  const createAbortController = () => {
    const controller = new AbortController();
    abortControllersRef.current.push(controller);
    return controller.signal;
  };

  const createPickupMarker = (
    detail,
    index,
    selectedTrip,
    handleMarkerClick
  ) => ({
    ...actualCheckPointMarker,
    position: {
      lat: Number(detail?.pickup_latitude),
      lng: Number(detail?.pickup_longitude),
    },
    label: {
      ...actualCheckPointMarker?.label,
      text: `P${index + 1}`,
    },
    onMarkerClick: () =>
      handleMarkerClick(
        `actual-checkpoint-info-window-${detail?.trip_id}-${detail?.event?.toLowerCase()}`,
        {
          lat: Number(detail?.pickup_latitude),
          lng: Number(detail?.pickup_longitude),
        },
        {
          Trip_ID: selectedTrip?.trip_id,
          Time: getFormattedTimeFromTimestamp(detail?.pickup_time),
          Vehicle_Number: selectedTrip?.registration,
        },
        {
          text: detail?.event
            ? `Scheduled ${detail?.event} point`
            : 'Scheduled pickup/drop point',
          bgColor: '#418BD6',
        },
        <AddressFetcher
          type="map"
          location={{
            latitude: detail?.pickup_latitude,
            longitude: detail?.pickup_longitude,
          }}
          fontSize="0.75rem"
          title={detail?.event ? `${detail?.event} location` : 'Location'}
        />
      ),
    key: `${detail?.emp_coord_id}-pickup-${index}`,
  });

  const createDropMarker = (
    detail,
    index,
    selectedTrip,
    handleMarkerClick
  ) => ({
    ...actualCheckPointMarker,
    position: {
      lat: Number(detail?.drop_latitude),
      lng: Number(detail?.drop_longitude),
    },
    label: {
      ...actualCheckPointMarker?.label,
      text: `D${index + 1}`,
    },
    onMarkerClick: () =>
      handleMarkerClick(
        `actual-checkpoint-info-window-${detail?.trip_id}-${detail?.event?.toLowerCase()}`,
        {
          lat: Number(detail?.drop_latitude),
          lng: Number(detail?.drop_longitude),
        },
        {
          Trip_ID: selectedTrip?.trip_id,
          Time: getFormattedTimeFromTimestamp(detail?.drop_time),
          Vehicle_Number: selectedTrip?.registration,
        },
        {
          text: detail?.event
            ? `Scheduled ${detail?.event} point`
            : 'Scheduled pickup/drop point',
          bgColor: '#418BD6',
        },
        <AddressFetcher
          type="map"
          location={{
            latitude: detail?.drop_latitude,
            longitude: detail?.drop_longitude,
          }}
          fontSize="0.75rem"
          title={detail?.event ? `${detail?.event} location` : 'Location'}
        />
      ),
    key: `${detail?.emp_coord_id}-drop-${index}`,
  });

  const getDmsMarker = (detail, selectedTrip, handleMarkerClick) => {
    if (
      !detail?.latitude ||
      !detail?.longitude ||
      (detail?.dms_type !== DMS_TYPE.DROWSINESS &&
        detail?.dms_type !== DMS_TYPE.DISTRACTION)
    ) {
      return null;
    }
    const dmsMarker =
      detail?.dms_type === DMS_TYPE.DROWSINESS
        ? driverDrowsinessMarker
        : driverDistractionMarker;

    return {
      ...dmsMarker,
      position: {
        lat: Number(detail?.latitude),
        lng: Number(detail?.longitude),
      },
      onMarkerClick: () =>
        handleMarkerClick(
          `${detail?.dms_type}-info-window-${detail?.dms_id}-${selectedTrip?.trip_id}`,
          {
            lat: Number(detail?.latitude),
            lng: Number(detail?.longitude),
          },
          {
            Trip_ID: selectedTrip?.trip_id,
            Time: getFormattedTimeFromTimestamp(detail?.dms_timestamp),
            Vehicle_Number: selectedTrip?.registration,
          },
          {
            text: `Driver ${
              detail?.dms_type === DMS_TYPE.DROWSINESS
                ? 'Drowsiness'
                : detail?.dms_type === DMS_TYPE.DISTRACTION
                  ? 'Distraction'
                  : ''
            }`,
            bgColor: 'var(--driver-anomaly-color, #ed0007)',
          },
          <AddressFetcher
            type="map"
            location={{
              latitude: detail?.latitude,
              longitude: detail?.longitude,
            }}
            fontSize="0.75rem"
          />
        ),
      key: `${detail?.dms_type}-${detail?.dms_id}-${selectedTrip?.trip_id}`,
    };
  };

  const handleDmsInfoWindow = (
    detail,
    selectedTrip,
    selectedAnomlayId,
    setInfoWindow
  ) => {
    if (selectedAnomlayId !== null && selectedAnomlayId === detail?.dms_id) {
      setInfoWindow({
        title: {
          text: `Driver ${
            detail?.dms_type === DMS_TYPE.DROWSINESS
              ? 'Drowsiness'
              : detail?.dms_type === DMS_TYPE.DISTRACTION
                ? 'Distraction'
                : ''
          }`,
          bgColor: 'var(--driver-anomaly-color, #ed0007)',
        },
        content: {
          Trip_ID: selectedTrip?.trip_id,
          Time: getFormattedTimeFromTimestamp(detail?.dms_timestamp),
        },
        position: {
          lat: Number(detail?.latitude),
          lng: Number(detail?.longitude),
        },
        children: (
          <>
            <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
              <b> {selectedTrip?.registration}</b>
            </Text>
            <AddressFetcher
              type="map"
              location={{
                latitude: detail?.latitude,
                longitude: detail?.longitude,
              }}
              fontSize="0.75rem"
            />
          </>
        ),
        key: `${detail?.dms_type}-info-window-${detail?.dms_id}-${selectedTrip?.trip_id}`,
        isVisible: true,
      });
    }
  };

  const getPickupDropData = async payload => {
    if (
      !isMountedRef.current ||
      currentTripIdRef.current !== payload.tripIds[0]
    ) {
      return;
    }

    const signal = createAbortController();
    try {
      const tripDetailsResponse =
        await API.getTripDetailsFromDeviceIdAndTripIds(payload, { signal });

      if (
        !isMountedRef.current ||
        currentTripIdRef.current !== payload.tripIds[0]
      ) {
        return;
      }

      if (
        tripDetailsResponse?.data?.message &&
        isMountedRef.current &&
        currentTripIdRef.current === selectedTrip?.trip_id
      ) {
        addErrorMessage(tripDetailsResponse?.data?.message);
      }

      if (tripDetailsResponse?.data?.length > 0) {
        tripDetailsResponse?.data?.forEach((detail, index) => {
          if (detail?.pickup_latitude && detail?.pickup_longitude) {
            setIsGPSMarkerUpdated(false);
            setMarkers(prev => [
              ...prev,
              createPickupMarker(
                detail,
                index,
                selectedTrip,
                handleMarkerClick
              ),
            ]);
            updateLivePassengersCount(detail);
            if (index === 0) {
              !pollingEnabled &&
                setCenter({
                  lat: Number(detail?.pickup_latitude),
                  lng: Number(detail?.pickup_longitude),
                });
            }
          }

          if (detail?.drop_latitude && detail?.drop_longitude) {
            setIsGPSMarkerUpdated(false);
            setMarkers(prev => [
              ...prev,
              createDropMarker(detail, index, selectedTrip, handleMarkerClick),
            ]);
          }
        });
      }
    } catch (error) {
      if (
        error.name !== 'AbortError' &&
        isMountedRef.current &&
        currentTripIdRef.current === payload.tripIds[0]
      ) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickup/drop data:', error);
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch pickup/drop details! Please try again.'
          )
        );
      }
    }
  };

  const getSOSAndRashdrivingdetails = async payload => {
    if (
      !isMountedRef.current ||
      currentTripIdRef.current !== payload.tripIds[0]
    ) {
      return;
    }

    try {
      const signal = createAbortController();
      const sosAndRashDrivingResponse =
        await API.getSOSAndRashDrivingDetailsFromDeviceIdAndTripIds(payload, {
          signal,
        });

      if (
        !isMountedRef.current ||
        currentTripIdRef.current !== payload.tripIds[0]
      ) {
        return;
      }

      if (
        sosAndRashDrivingResponse?.data?.message &&
        isMountedRef.current &&
        currentTripIdRef.current === selectedTrip?.trip_id
      ) {
        addErrorMessage(sosAndRashDrivingResponse?.data?.message);
      }

      if (
        sosAndRashDrivingResponse?.data?.sos_data ||
        sosAndRashDrivingResponse?.data?.rash_data ||
        sosAndRashDrivingResponse?.data?.dms_data ||
        sosAndRashDrivingResponse?.data?.overspeed_data
      ) {
        let sosResponse = [];
        sosAndRashDrivingResponse?.data?.sos_data?.forEach(detail => {
          if (detail?.latitude && detail?.longitude) {
            sosResponse.push({
              ...sosAlertMarker,
              position: {
                lat: Number(detail?.latitude),
                lng: Number(detail?.longitude),
              },
              onMarkerClick: () =>
                handleMarkerClick(
                  `sos-info-window-${detail?.messageid}-${selectedTrip?.trip_id}`,
                  {
                    lat: Number(detail?.latitude),
                    lng: Number(detail?.longitude),
                  },
                  {
                    Trip_ID: selectedTrip?.trip_id,
                    Time: getFormattedTimeFromTimestamp(detail?.sos_created_at),
                    Vehicle_Number: selectedTrip?.registration,
                  },
                  {
                    text: 'SOS Alert',
                    bgColor: ANOMALY_BG_COLOR.SOS,
                  },
                  <AddressFetcher
                    type="map"
                    location={{
                      latitude: detail?.latitude,
                      longitude: detail?.longitude,
                    }}
                    fontSize="0.75rem"
                  />
                ),
              key: `sos-${detail?.messageid}-${selectedTrip?.trip_id}`,
            });
            if (
              selectedAnomlayId !== null &&
              selectedAnomlayId === detail?.messageid
            ) {
              setInfoWindow({
                title: {
                  text: 'SOS Alert',
                  bgColor: ANOMALY_BG_COLOR.SOS,
                },
                content: {
                  Trip_ID: selectedTrip?.trip_id,
                  Time: getFormattedTimeFromTimestamp(detail?.sos_created_at),
                },
                position: {
                  lat: Number(detail?.latitude),
                  lng: Number(detail?.longitude),
                },
                children: (
                  <>
                    <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
                      <b> {selectedTrip?.registration}</b>
                    </Text>
                    <AddressFetcher
                      type="map"
                      location={{
                        latitude: detail?.latitude,
                        longitude: detail?.longitude,
                      }}
                      fontSize="0.75rem"
                    />
                  </>
                ),
                key: `sos-info-window-${detail?.messageid}-${selectedTrip?.trip_id}`,
                isVisible: true,
              });
            }
          }
        });

        let rashDrivingResponse = [];
        sosAndRashDrivingResponse?.data?.rash_data?.forEach(detail => {
          if (detail?.latitude && detail?.longitude) {
            rashDrivingResponse.push({
              ...rashDrivingMarker,
              position: {
                lat: Number(detail?.latitude),
                lng: Number(detail?.longitude),
              },
              onMarkerClick: () =>
                handleMarkerClick(
                  `rash-driving-info-window-${detail?.nudge_id}-${selectedTrip?.trip_id}`,
                  {
                    lat: Number(detail?.latitude),
                    lng: Number(detail?.longitude),
                  },
                  {
                    Trip_ID: selectedTrip?.trip_id,
                    Time: getFormattedTimeFromTimestamp(
                      detail?.rashdriving_timestamp
                    ),
                    Vehicle_Number: selectedTrip?.registration,
                  },
                  {
                    text: 'Rash Driving',
                    bgColor: ANOMALY_BG_COLOR.MAJOR,
                  },
                  <AddressFetcher
                    type="map"
                    location={{
                      latitude: detail?.latitude,
                      longitude: detail?.longitude,
                    }}
                    fontSize="0.75rem"
                  />
                ),
              key: `rash-driving-${detail?.rashdriving_id}-${selectedTrip?.trip_id}`,
            });
            if (
              selectedAnomlayId !== null &&
              selectedAnomlayId === detail?.rashdriving_id
            ) {
              setInfoWindow({
                title: {
                  text: 'Rash Driving',
                  bgColor: ANOMALY_BG_COLOR.MAJOR,
                },
                content: {
                  Trip_ID: selectedTrip?.trip_id,
                  Time: getFormattedTimeFromTimestamp(
                    detail?.rashdriving_timestamp
                  ),
                },
                position: {
                  lat: Number(detail?.latitude),
                  lng: Number(detail?.longitude),
                },
                children: (
                  <>
                    <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
                      <b> {selectedTrip?.registration}</b>
                    </Text>
                    <AddressFetcher
                      type="map"
                      location={{
                        latitude: detail?.latitude,
                        longitude: detail?.longitude,
                      }}
                      fontSize="0.75rem"
                    />
                  </>
                ),
                key: `rash-driving-info-window-${detail?.rashdriving_id}-${selectedTrip?.trip_id}`,
                isVisible: true,
              });
            }
          }
        });

        let dmsResponse = [];
        sosAndRashDrivingResponse?.data?.dms_data?.forEach(detail => {
          if (detail?.dms_type === DMS_TYPE.DISTRACTION) return;
          const marker = getDmsMarker(detail, selectedTrip, handleMarkerClick);
          if (marker) {
            dmsResponse.push(marker);
            handleDmsInfoWindow(
              detail,
              selectedTrip,
              selectedAnomlayId,
              setInfoWindow
            );
          }
        });

        let doorOpenResponse = [];
        sosAndRashDrivingResponse?.data?.door_open_data?.forEach(detail => {
          if (detail?.latitude && detail?.longitude) {
            doorOpenResponse.push({
              ...doorOpenMarker,
              position: {
                lat: Number(detail?.latitude),
                lng: Number(detail?.longitude),
              },
              onMarkerClick: () =>
                handleMarkerClick(
                  `door-marker-info-window-${detail?.door_open_id}-${selectedTrip?.trip_id}`,
                  {
                    lat: Number(detail?.latitude),
                    lng: Number(detail?.longitude),
                  },
                  {
                    Trip_ID: selectedTrip?.trip_id,
                    Time: getFormattedTimeFromTimestamp(
                      detail?.door_open_timestamp
                    ),
                    Vehicle_Number: selectedTrip?.registration,
                  },
                  {
                    text: 'Door Open',
                    bgColor: ANOMALY_BG_COLOR.MINOR,
                  },
                  <AddressFetcher
                    type="map"
                    location={{
                      latitude: detail?.latitude,
                      longitude: detail?.longitude,
                    }}
                    fontSize="0.75rem"
                  />
                ),
              key: `door-open-${detail?.door_open_id}-${selectedTrip?.trip_id}`,
            });
            if (
              selectedAnomlayId !== null &&
              selectedAnomlayId === detail?.door_open_id
            ) {
              setInfoWindow({
                title: {
                  text: 'Door Open',
                  bgColor: ANOMALY_BG_COLOR.MINOR,
                },
                content: {
                  Trip_ID: selectedTrip?.trip_id,
                  Time: getFormattedTimeFromTimestamp(
                    detail?.door_open_timestamp
                  ),
                },
                position: {
                  lat: Number(detail?.latitude),
                  lng: Number(detail?.longitude),
                },
                children: (
                  <>
                    <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
                      <b> {selectedTrip?.registration}</b>
                    </Text>
                    <AddressFetcher
                      type="map"
                      location={{
                        latitude: detail?.latitude,
                        longitude: detail?.longitude,
                      }}
                      fontSize="0.75rem"
                    />
                  </>
                ),
                key: `door-marker-info-window-${detail?.door_open_id}-${selectedTrip?.trip_id}`,
                isVisible: true,
              });
            }
          }
        });

        let overspeedResponse = [];
        sosAndRashDrivingResponse?.data?.overspeed_data?.forEach(detail => {
          if (detail?.latitude && detail?.longitude) {
            overspeedResponse.push({
              ...overspeedMarker,
              position: {
                lat: Number(detail?.latitude),
                lng: Number(detail?.longitude),
              },
              onMarkerClick: () =>
                handleMarkerClick(
                  `over-speed-marker-info-window-${detail?.overspeed_id}-${selectedTrip?.trip_id}`,
                  {
                    lat: Number(detail?.latitude),
                    lng: Number(detail?.longitude),
                  },
                  {
                    Trip_ID: selectedTrip?.trip_id,
                    Time: getFormattedTimeFromTimestamp(
                      detail?.overspeed_timestamp
                    ),
                    Vehicle_Number: selectedTrip?.registration,
                  },
                  {
                    text: 'Over Speeding',
                    bgColor: ANOMALY_BG_COLOR.MINOR,
                  },
                  <AddressFetcher
                    type="map"
                    location={{
                      latitude: detail?.latitude,
                      longitude: detail?.longitude,
                    }}
                    fontSize="0.75rem"
                  />
                ),
              key: `over-speed-${detail?.overspeed_id}-${selectedTrip?.trip_id}`,
            });
            if (
              selectedAnomlayId !== null &&
              selectedAnomlayId === detail?.dms_id
            ) {
              setInfoWindow({
                title: {
                  text: `Driver ${
                    detail?.dms_type === DMS_TYPE.DROWSINESS
                      ? 'Drowsiness'
                      : detail?.dms_type === DMS_TYPE.DISTRACTION
                        ? 'Distraction'
                        : ''
                  }`,
                  bgColor: ANOMALY_BG_COLOR.CRITICAL,
                },
                content: {
                  Trip_ID: selectedTrip?.trip_id,
                  Time: getFormattedTimeFromTimestamp(detail?.dms_timestamp),
                },
                position: {
                  lat: Number(detail?.latitude),
                  lng: Number(detail?.longitude),
                },
                children: (
                  <>
                    <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
                      <b> {selectedTrip?.registration}</b>
                    </Text>
                    <AddressFetcher
                      type="map"
                      location={{
                        latitude: detail?.latitude,
                        longitude: detail?.longitude,
                      }}
                      fontSize="0.75rem"
                    />
                  </>
                ),
                key: `${detail?.dms_type}-info-window-${detail?.dms_id}-${selectedTrip?.trip_id}`,
                isVisible: true,
              });
            }
          }
        });

        if (
          isMountedRef.current &&
          currentTripIdRef.current === payload.tripIds[0]
        ) {
          setIsGPSMarkerUpdated(false);
          setMarkers(prev => [
            ...prev,
            ...sosResponse,
            ...rashDrivingResponse,
            ...dmsResponse,
            ...doorOpenResponse,
            ...overspeedResponse,
          ]);
          updateLastUpdatedTime(Date.now());
        }
      }
    } catch (error) {
      if (
        error.name !== 'AbortError' &&
        isMountedRef.current &&
        currentTripIdRef.current === payload.tripIds[0]
      ) {
        // eslint-disable-next-line no-console
        console.error('Error fetching anomaly data:', error);
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch anomaly details! Please try again.'
          )
        );
      }
    }
  };

  const shouldAbortGPSFetch = (signal, tripId) =>
    signal.aborted ||
    currentTripIdRef.current !== tripId ||
    !isMountedRef.current;

  const shouldShowLoader = (showLoader, tripId) =>
    showLoader && isMountedRef.current && currentTripIdRef.current === tripId;

  const shouldSetPathData = (initialGpsLoadCompleteRef, tripId) =>
    !initialGpsLoadCompleteRef.current &&
    isMountedRef.current &&
    currentTripIdRef.current === tripId;

  const shouldSetIncrementalPathData = (initialGpsLoadCompleteRef, tripId) =>
    initialGpsLoadCompleteRef.current &&
    isMountedRef.current &&
    currentTripIdRef.current === tripId;

  const setSuccessMessage = tripId => {
    setGpsLoadingMessage({
      message: 'GPS data loaded successfully!',
      type: 'success',
    });
    setTimeout(() => {
      if (isMountedRef.current && currentTripIdRef.current === tripId) {
        setGpsLoadingMessage({ message: null });
      }
    }, 3000);
  };

  const getGPSData = async (payload, showLoader = true) => {
    setGpsLoadingMessage({ message: null });

    const tripId = selectedTrip?.trip_id;
    if (
      !tripId ||
      tripId !== payload?.tripIds[0] ||
      currentTripIdRef.current !== tripId ||
      !isMountedRef.current
    ) {
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    abortFetchRef.current = () => controller.abort();
    let gpsDataPageNumber = 1;
    let allPaths = [];
    let latestGpsPoint = null;

    const handleError = msg => {
      gpsDataPageNumber = null;
      if (isMountedRef.current && currentTripIdRef.current === tripId) {
        addErrorMessage(msg);
      }
    };

    const handleGPSResults = (gpsDataResponse, pathPoints) => {
      allPaths.push(...pathPoints);
      if (shouldSetIncrementalPathData(initialGpsLoadCompleteRef, tripId)) {
        setPathData(prev => ({
          ...prev,
          path: [...prev.path, ...pathPoints],
        }));
        if (gpsDataPageNumber === 1 && pathPoints.length > 0)
          setCenter({ lat: pathPoints[0]?.lat, lng: pathPoints[0]?.lng });
      }
      if (gpsDataResponse?.data?.results?.length > 0) {
        const lastResult =
          gpsDataResponse?.data?.results[
            gpsDataResponse?.data?.results.length - 1
          ];
        latestGpsPoint = {
          latitude: lastResult.latitude,
          longitude: lastResult.longitude,
          trip_iD: tripId,
        };
      }
      pastGpsDataLoadedRef.current = true;
    };

    const updatePathDataIfNeeded = () => {
      if (shouldSetPathData(initialGpsLoadCompleteRef, tripId)) {
        setPathData(prev => ({
          ...prev,
          path: allPaths,
        }));
      }
    };

    const updateGPSMarkerIfNeeded = () => {
      if (
        latestGpsPoint &&
        activeTabKey === VEHICLE_STATUS.Active &&
        pageActiveKey === 'monitoring'
      ) {
        updateGPSMarkers(latestGpsPoint);
      }
    };

    const showLoaderIfNeeded = () => {
      if (shouldShowLoader(showLoader, tripId)) {
        setSuccessMessage(tripId);
      }
    };

    try {
      if (shouldSetIncrementalPathData(initialGpsLoadCompleteRef, tripId)) {
        setGpsLoadingMessage({ message: 'Loading GPS data...', type: 'info' });
      }

      while (gpsDataPageNumber !== null) {
        if (shouldAbortGPSFetch(signal, tripId)) return;

        const gpsDataResponse = await API.getGPSDataFromDeviceIdAndTripIds(
          payload,
          gpsDataPageNumber,
          { signal }
        );

        if (gpsDataResponse?.data?.message) {
          handleError(gpsDataResponse?.data?.message);
          continue;
        }

        if (
          gpsDataResponse?.data?.results?.length > 0 &&
          currentTripIdRef.current === tripId &&
          isMountedRef.current
        ) {
          const pathPoints = gpsDataResponse?.data?.results?.map(detail => ({
            lat: Number(detail?.latitude),
            lng: Number(detail?.longitude),
          }));
          handleGPSResults(gpsDataResponse, pathPoints);
        }

        gpsDataPageNumber = gpsDataResponse?.data?.next_page_number;
      }

      updatePathDataIfNeeded();
      updateGPSMarkerIfNeeded();
      showLoaderIfNeeded();
      updateLastUpdatedTime(Date.now());
    } catch (error) {
      if (error.name !== 'AbortError' && shouldShowLoader(showLoader, tripId)) {
        // eslint-disable-next-line no-console
        console.error('Error fetching GPS data:', error);
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch trip details! Please try again.'
          )
        );
      }
    } finally {
      setGpsLoadingMessage({ message: null });
    }
  };

  const shouldPollSOS = Boolean(
    selectedTrip?.trip_id &&
      pageActiveKey === 'monitoring' &&
      activeTabKey === VEHICLE_STATUS.Active
  );

  const shouldPollGPS = Boolean(
    selectedTrip?.trip_id &&
      pageActiveKey === 'monitoring' &&
      activeTabKey === VEHICLE_STATUS.Active &&
      !initialGpsLoadCompleteRef.current &&
      pollingEnabled
  );

  useApiPolling(
    () =>
      getSOSAndRashdrivingdetails({
        deviceId: selectedTrip?.ridebuddy_deviceid,
        tripIds: [selectedTrip?.trip_id],
      }),
    shouldPollSOS,
    [shouldPollSOS],
    false,
    1 * 60 * 1000, // Poll every 1 minute
    true
  );

  useApiPolling(
    () =>
      getGPSData(
        {
          deviceId: selectedTrip?.ridebuddy_deviceid,
          tripIds: [selectedTrip?.trip_id],
        },
        false
      ),
    shouldPollGPS,
    [shouldPollGPS],
    false,
    1 * 60 * 1000, // Poll every 1 minute
    true
  );

  return (
    <GoogleMaps
      gpsLoadingMessage={gpsLoadingMessage}
      showLegend={true}
      isGPSLocationUpdated={isGPSMarkerUpdated}
      isMapLegendVertical={!isFullScreen}
      showCustomFullScreenControl={showCustomFullScreenControl}
      CustomFullScreenControl={
        <FullScreenToggle
          isFullScreen={isFullScreen}
          onClick={onFullScreenToggle}
        />
      }
      infoWindow={infoWindow}
      closeInfoWindow={() =>
        setInfoWindow({
          isVisible: false,
        })
      }
      markers={markers}
      center={center}
      polylines={pathData && [pathData]}
      enableRecenter={!shouldPollGPS}
      showMarkerHoverEffectOnHovered
    />
  );
};

TripTracking.propTypes = {
  isFullScreen: PropTypes.bool.isRequired,
  onFullScreenToggle: PropTypes.func.isRequired,
  selectedTrip: PropTypes.shape({
    trip_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ridebuddy_deviceid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    registration: PropTypes.string,
  }),
  activeTabKey: PropTypes.string,
  pageActiveKey: PropTypes.string,
  updateLastUpdatedTime: PropTypes.func,
  selectedAnomlayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCustomFullScreenControl: PropTypes.bool,
};

TripTracking.defaultProps = {
  selectedTrip: null,
  activeTabKey: '',
  pageActiveKey: '',
  selectedAnomlayId: null,
  showCustomFullScreenControl: false,
};

export default React.memo(TripTracking);
